import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthIntegrationTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { GrowthAdminService, GrowthConnectorDto, GrowthConnectorSyncRunDto } from './growth-admin.service';
import {
  GROWTH_CONNECTOR_SYNC_RUN_STATUSES,
  growthConnectorSyncSummaryFields
} from './growth-integration-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-integration-sync-status',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    StructuredJsonDisplayComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DialogModule,
    RouterLink,
    AinDatePipe
  ],
  template: `
    <ain-page [title]="t('growth.integration.syncStatus.title')" [description]="t('growth.integration.syncStatus.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.integration.syncStatus.help.title'"
        [descriptionKey]="'growth.integration.syncStatus.help.description'"
        [bulletKeys]="['growth.integration.syncStatus.help.bullets.receipts', 'growth.integration.syncStatus.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <p-select
            [options]="connectorOptions()"
            [(ngModel)]="connectorKeyFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.integration.syncStatus.filterConnector')" />
          <p-select
            [options]="statusOptions()"
            [(ngModel)]="statusFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.integration.syncStatus.filterStatus')" />
          <button pButton type="button" [label]="t('growth.integration.syncStatus.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.integration.syncStatus.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.integration.syncStatus.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          <a class="link-btn" routerLink="/growth/integration/connectors">{{ t('growth.integration.syncStatus.openConnectors') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.integration.syncStatus.columns.connectorKey') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.direction') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.status') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.startedAtUtc') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.finishedAtUtc') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.providerEventId') }}</th>
              <th>{{ t('growth.integration.syncStatus.columns.leadId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row)">{{ row.connectorKey }}</button>
              </td>
              <td>{{ enumLabel('direction', row.direction) }}</td>
              <td>{{ enumLabel('syncStatus', row.status) }}</td>
              <td>{{ row.startedAtUtc | ainDate:'datetime' }}</td>
              <td>{{ row.finishedAtUtc ? (row.finishedAtUtc | ainDate:'datetime') : '-' }}</td>
              <td>{{ row.providerEventId ?? '-' }}</td>
              <td>{{ row.leadId ?? '-' }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.integration.syncStatus.detailDialog.title')"
      [visible]="detailDialogVisible()"
      (visibleChange)="detailDialogVisible.set($event)"
      [modal]="true"
      [style]="{ width: '720px' }">
      @if (selected()) {
        <ain-info-hint
          [titleKey]="'growth.integration.syncStatus.detailDialog.help.title'"
          [descriptionKey]="'growth.integration.syncStatus.detailDialog.help.description'" />
        <div class="detail-grid">
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.connectorKey') }}</span>
            <input pInputText type="text" [ngModel]="selected()!.connectorKey" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.direction') }}</span>
            <input pInputText type="text" [ngModel]="enumLabel('direction', selected()!.direction)" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.status') }}</span>
            <input pInputText type="text" [ngModel]="enumLabel('syncStatus', selected()!.status)" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.startedAtUtc') }}</span>
            <input pInputText type="text" [ngModel]="selected()!.startedAtUtc | ainDate:'datetime'" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.finishedAtUtc') }}</span>
            <input pInputText type="text" [ngModel]="selected()!.finishedAtUtc ? (selected()!.finishedAtUtc | ainDate:'datetime') : '-'" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.providerEventId') }}</span>
            <input pInputText type="text" [ngModel]="selected()!.providerEventId ?? '-'" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('growth.integration.syncStatus.columns.leadId') }}</span>
            <input pInputText type="text" [ngModel]="selected()!.leadId ?? '-'" [disabled]="true" />
          </label>
          @if (selected()!.error) {
            <label class="full">
              <span>{{ t('growth.integration.syncStatus.columns.error') }}</span>
              <input pInputText type="text" [ngModel]="selected()!.error" [disabled]="true" />
            </label>
          }
        </div>
        <section class="summary-section">
          <h4>{{ t('growth.integration.syncStatus.detailDialog.summary') }}</h4>
          <ain-structured-json-display [fields]="summaryFields" [payloadJson]="selected()!.summaryJson" />
        </section>
        <div class="detail-actions">
          <a class="link-btn" [routerLink]="['/growth/integration/connectors', selected()!.connectorKey]">{{ t('growth.integration.syncStatus.detailDialog.openConnector') }}</a>
        </div>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.close')" severity="secondary" (click)="detailDialogVisible.set(false)"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .detail-grid label { display: grid; gap: 6px; }
    .detail-grid .full { grid-column: 1 / -1; }
    .summary-section { margin-top: 12px; display: grid; gap: 8px; }
    .summary-section h4 { margin: 0; font-size: 14px; }
    .detail-actions { margin-top: 10px; }
  `]
})
export class GrowthIntegrationSyncStatusComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly summaryFields = growthConnectorSyncSummaryFields;

  readonly rows = signal<GrowthConnectorSyncRunDto[]>([]);
  readonly filteredRows = signal<GrowthConnectorSyncRunDto[]>([]);
  readonly connectors = signal<GrowthConnectorDto[]>([]);
  readonly selected = signal<GrowthConnectorSyncRunDto | null>(null);
  readonly errorMessage = signal('');
  readonly detailDialogVisible = signal(false);

  connectorKeyFilter: string | null = null;
  statusFilter: string | null = null;

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.connectorKeyFilter = this.route.snapshot.queryParamMap.get('connectorKey');

    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthIntegrationChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadConnectors();
    this.load();
  }

  connectorOptions(): Array<{ label: string; value: string }> {
    return this.connectors().map(item => ({
      value: item.connectorKey,
      label: `${item.displayName} (${item.connectorKey})`
    }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_SYNC_RUN_STATUSES.map(value => ({
      value,
      label: this.enumLabel('syncStatus', value)
    }));
  }

  enumLabel(group: 'direction' | 'syncStatus', value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    if (group === 'direction') {
      return this.t(`growth.integration.enums.direction.${value}`);
    }
    return this.t(`growth.integration.enums.syncStatus.${value}`);
  }

  loadConnectors(): void {
    this.api.listGrowthConnectors().subscribe({
      next: rows => this.connectors.set(rows),
      error: () => this.connectors.set([])
    });
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listGrowthConnectorSyncStatus(this.connectorKeyFilter).subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.integration.syncStatus.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    let filtered = this.rows();
    if (this.statusFilter) {
      filtered = filtered.filter(item => item.status === this.statusFilter);
    }
    this.filteredRows.set(filtered);
    const currentId = this.selected()?.id;
    this.selected.set(filtered.find(item => item.id === currentId) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthConnectorSyncRunDto | null): void {
    this.selected.set(row);
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row);
  }

  openDetail(row: GrowthConnectorSyncRunDto): void {
    this.selected.set(row);
    this.detailDialogVisible.set(true);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIntegrationTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
